import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  assetPreviewMediaSx,
  getAssetPreviewModesForKind,
  resolveAssetPreviewKind,
} from "./assetConstants";

const AssetPreview = ({ url, loading, contentType = "" }) => {
  const [modeIndex, setModeIndex] = useState(0);
  const videoLoadedRef = useRef(false);

  const previewModes = useMemo(() => {
    const kind = resolveAssetPreviewKind(url, contentType);
    return getAssetPreviewModesForKind(kind);
  }, [url, contentType]);

  useEffect(() => {
    setModeIndex(0);
    videoLoadedRef.current = false;
  }, [url, contentType]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!url) {
    return null;
  }

  const currentMode = previewModes[modeIndex] ?? "open";

  const tryNextMode = () => {
    setModeIndex((prev) => (prev >= previewModes.length - 1 ? prev : prev + 1));
  };

  const openAssetFallback = (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Inline preview is not available for this file type. Open the asset in a new tab to
        view or download it.
      </Typography>
      <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
        Open asset
      </Link>
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 2, borderRadius: 1, bgcolor: "#fafafa", maxWidth: 640 }}
    >
      {currentMode === "video" ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Video preview — use the controls below to play
          </Typography>
          <Box
            key={url}
            component="video"
            src={url}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={() => {
              videoLoadedRef.current = true;
            }}
            onError={() => {
              if (!videoLoadedRef.current) tryNextMode();
            }}
            sx={assetPreviewMediaSx}
          />
        </Box>
      ) : currentMode === "image" ? (
        <Card sx={{ boxShadow: "none", bgcolor: "transparent" }}>
          <CardMedia
            component="img"
            image={url}
            alt="Asset preview"
            onError={tryNextMode}
            sx={{ ...assetPreviewMediaSx, objectFit: "contain" }}
          />
        </Card>
      ) : (
        openAssetFallback
      )}
    </Paper>
  );
};

export default AssetPreview;
